<template>
    <div>
        <div class="plain-card">
            <h5>
                <span @click.prevent="toggleFilters($event)" class="pointer">
                    <i class="fa fa-caret-down pull-left active pen-pink"></i>
                    {{ show_filters ? uLang('app.hide_filters') : uLang('app.show_filters') }}
                </span>
            </h5>

            <template v-if="show_filters">

                <!-- Filter presets -->
                <div class="row form-group">
                    
                    <!-- Date filter presets -->
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.period') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <b-button-group>
                                <b-button
                                    v-for="(preset, key) in date_filter_presets"
                                    @click="applyDatePreset(preset.months, key)"
                                    :key="key"
                                    :class="key === selected_date_preset ? 'pressed' : ''"
                                    v-text="preset.name"
                                    class="datePresetButton"
                                    variant="brand"
                                />
                            </b-button-group>
                        </div>
                    </div>

                    <!-- Only with time -->
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-12 col-sm-12 col-form-label">{{ uLang('app.Only with TimeLogs data') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <b-button-group>
                                <!-- Show all -->
                                <b-button
                                    class="datePresetButton"
                                    variant="brand"
                                    @click="filterOnlyWithTimeLogsStatus(false)"
                                    :class="!filters.only_with_time_logs ? 'pressed' : ''"
                                    v-text="uLang('app.All')"
                                />
                                <!-- Show only with TimeLogs data -->
                                <b-button
                                    class="datePresetButton"
                                    variant="brand"
                                    @click="filterOnlyWithTimeLogsStatus(true)"
                                    :class="filters.only_with_time_logs ? 'pressed' : ''"
                                    v-text="uLang('app.Only with TimeLogs data')"
                                />
                            </b-button-group>
                        </div>
                    </div>
                </div>

                <!-- Trajectory types and projects -->
                <div class="row form-group">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.projects') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedProjects"
                                :options="availableFilterOptions.projects"
                                :placeholder="uLang('app.projects')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="2"
                                :limit-text="limitText('projects')"
                                id="projects"
                                name="projects"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                                ref="multiSelectProject"
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionProject()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.projects.length"
                                         @click="clearFilter('projects', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.trajectory_types') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedTrajectoryTypes"
                                :options="availableFilterOptions.trajectory_types"
                                :placeholder="uLang('app.trajectory_types')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="3"
                                :limit-text="limitText('trajectory_types')"
                                id="trajectory_types"
                                name="trajectory_types"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                                ref="multiSelectTrajectoryType"
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionTrajectoryType()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.trajectory_types.length"
                                         @click="clearFilter('trajectory_types', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>
                </div>

                <!-- Trajectories and clients -->
                <div class="row form-group">
                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.clients') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedClients"
                                :options="availableFilterOptions.clients"
                                :placeholder="uLang('app.clients')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="3"
                                :limit-text="limitText('clients')"
                                id="clients"
                                name="clients"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                                ref="multiSelectClient"
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionClient()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.clients.length"
                                         @click="clearFilter('clients', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>

                    <div class="col-md-6 col-sm-12">
                        <label class="col-md-2 col-sm-12 col-form-label">{{ uLang('app.trajectories') }}</label>
                        <div class="col-md-9 col-xl-10 col-sm-12">
                            <multi-select
                                v-model="selectedTrajectories"
                                :options="availableFilterOptions.trajectories"
                                :placeholder="uLang('app.trajectories')"
                                :close-on-select="false"
                                :show-labels="false"
                                @input="updateData()"
                                :limit="2"
                                :limit-text="limitText('trajectories')"
                                id="trajectories"
                                name="trajectories"
                                track-by="id"
                                label="name"
                                return="id"
                                multiple
                                ref="multiSelectTrajectory"
                            >
                                <template slot="tag" slot-scope="props">
                                    <span class="multiselect__tag" :title="props.option && props.option.name">
                                        <span>{{ props.option && props.option.name }}</span>
                                        <i class="multiselect__tag-icon" aria-hidden="true" @mousedown.prevent="props.remove(props.option)"></i>
                                    </span>
                                </template>
                                <template slot="beforeList">
                                    <li class="multiselect__element multiselect__element__idplein" @click.prevent.stop="handleCustomOptionTrajectory()"><span class="multiselect__option"><span>Selecteer alles</span></span></li>
                                </template>
                                <template slot="clear" slot-scope="props">
                                    <div v-show="filters.trajectories.length"
                                         @click="clearFilter('trajectories', [])"
                                         class="multiselect__clear"
                                    ></div>
                                </template>
                            </multi-select>
                        </div>
                    </div>
                </div>
                
            </template>
        </div>


        <div class="plain-card-2">
            <scrollable-second :loading="loading">
                <template slot="header">

                    <!-- Trajectory_enrolment_id

                        Candidate name

                        Trajectory name

                        Project name

                        Total time logged -->

                    <tr>
                        <th class="icon-sort-header" @click="sortTable('enrolment_id')">{{ uLang('app.enrolment') }} ({{ num_all_enrolments }}) 
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'enrolment_id' && sortOrder === 'asc', '-descending': sortColumn === 'enrolment_id' && sortOrder === 'desc'}"></span>
                        </th>


                        <th class="icon-sort-header" @click="sortTable('text_candidate_name')">{{ uLang('app.candidate') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_candidate_name' && sortOrder === 'asc', '-descending': sortColumn === 'text_candidate_name' && sortOrder === 'desc'}"></span>
                        </th>

                        <th class="icon-sort-header" @click="sortTable('text_trajectory_name')">{{ uLang('app.trajectory') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_trajectory_name' && sortOrder === 'asc', '-descending': sortColumn === 'text_trajectory_name' && sortOrder === 'desc'}"></span>
                        </th>

                        <th class="icon-sort-header" @click="sortTable('text_project_name')">Project
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_project_name' && sortOrder === 'asc', '-descending': sortColumn === 'text_project_name' && sortOrder === 'desc'}"></span>
                        </th>

                        <th class="icon-sort-header" @click="sortTable('text_time_logged')">{{ uLang('app.report_time_logged') }}
                            <span class="icon-sort" :class="{'-ascending': sortColumn === 'text_time_logged' && sortOrder === 'asc', '-descending': sortColumn === 'text_time_logged' && sortOrder === 'desc'}"></span>
                        </th>
                    </tr>

                    <!-- Averages -->
                    <tr class="summary-1">
                        <th colspan="4">{{ uLang('app.average') }}</th>
                        <th>{{ decimalToDutchTime(average_time_logged) }}</th>
                    </tr>

                    <!-- Totals -->
                    <tr class="summary-2">
                        <th colspan="4">{{ uLang('app.total') }}</th>
                        <th>{{ decimalToDutchTime(sum_time_logged) }}</th>
                    </tr>
                </template>

                <tbody slot="body">
                    <tr v-for="enrolment in enrolments" :key="enrolment.id">

                        <td>
                            <template>
                                <a :href="'/admin/trajectory_enrolments/'+ enrolment.enrolment_id" target="_blank">{{ enrolment.enrolment_id }}</a>
                            </template>
                        </td>

                        <td>
                            <template>
                                <a
                                    :href="`/admin/relations/${enrolment.relation_id}`"
                                    target="_blank"
                                    >
                                    {{ [enrolment.first_letters, enrolment.first_name, enrolment.prefix, enrolment.last_name]
                                        .filter(Boolean) // Trick to remove null, undefined, '', and 0
                                        .join(' ') }}
                                </a>
                            </template>
                        </td>
                        
                        <td>
                            <template>
                                <a :href="'/admin/trajectories/'+ enrolment.trajectory_id" target="_blank">{{ enrolment.trajectory_name }}</a>
                            </template>
                        </td>

                        <td>
                            <template>
                                <a :href="'/admin/projects/'+ enrolment.trajectory_project_id" target="_blank">{{ enrolment.trajectory_project_name }}</a>
                            </template>
                        </td>

                        <td>
                            <template>
                                {{ decimalToDutchTime(enrolment.time_logged) }}
                            </template>
                        </td>

                    </tr>
                </tbody>
            </scrollable-second>
        </div>
    </div>
</template>

<script>
    import MultiSelect from 'vue-multiselect';
    import { TrajectoryManagerSelect } from '~/Form/elements';
    import { ReaLoader } from "~/components/helpers";
    import { mapGetters } from 'vuex'
    import ManagementInfoResultRow from "~/components/tables/table-rows/ManagementInfoResultRow"
    import { ToggleFiltersMixin } from '~/mixins/management-info'
    import SettingSelect from "../../../Form/elements/setting-select"
    import { debounce } from 'lodash'
    import ScrollableSecond from '~/components/tables/templates/scrollablesecond'
    import { SuccessAlert } from '~/Swals'

    export default {
        name: 'enrolments-timelogs',
        components: {
            SettingSelect,
            ManagementInfoResultRow,
            ReaLoader,
            MultiSelect,
            TrajectoryManagerSelect,
            ScrollableSecond
        },
        mixins: [ToggleFiltersMixin],
        watch: {
            sortColumn() {
                this.sortData();
            },
            sortOrder() {
                this.sortData();
            },
        },
        data() {
            return {
                show_filters: true,
                loading: true,
                loadingFilters: false,
                enrolments: [],
                selected_date_preset: null,
                date_filter_presets: {
                    all: {
                        months: null,
                        name: this.uLang('app.all_results')
                    },
                    three: {
                        months: 3,
                        name: `${ this.uLang('app.current_calendar_year')}`,
                    },
                    six: {
                        months: 6,
                        name: `${ this.uLang('app.previous_calendar_year')}`,
                    },
                    twelve: {
                        months: 12,
                        name: `12 ${ this.uLang('app.months') }`,
                    },
                    custom: {
                        months: null,
                        name: this.uLang('app.custom_input'),
                    },
                },
                filters: {
                    date_from: null,
                    date_to: null,
                    trajectory_types: [],
                    projects: [],
                    trajectories: [],
                    clients: [],
                    only_with_time_logs: false,
                },
                availableFilterOptions: {
                    trajectory_types: [],
                    projects: [],
                    trajectories: [],
                    clients: [],
                },
                num_all_enrolments: 0,
                sum_time_logged: 0,
                average_time_logged: 0,

                text_time_logged: 0,

                sortColumn: 'enrolment_id',
                sortOrder: 'asc',

                selectedDataOverdue: null,

                selectedDateTextInPikaday: null,
                pikadayOptions: {
                    format: 'DD-MM-YYYY',
                    minDate: moment().add(1, 'days').toDate(),
                }
            }
        },
        async created() {

            // Restrict access to coaches
            if (this.isCoach()) {
                console.log('Restricted to coaches');
                this.$router.push({ name: 'admin.reports.index' });
                return;
            }

            this.getTrajectoryTypes()
            this.defaultFilters()
        },
        computed: {
            ...mapGetters(['authUser', 'settingByName']),
            selectedTrajectoryTypes: {
                get() {
                    return this.selectedEntityGetter('trajectory_types')
                },
                set(trajectory_types) {
                    this.selectedEntitySetter(trajectory_types, 'trajectory_types')
                }
            },
            selectedProjects: {
                get() {
                    return this.selectedEntityGetter('projects')
                },
                set(projects) {
                    this.selectedEntitySetter(projects, 'projects')
                }
            },
            selectedTrajectories: {
                get() {
                    return this.selectedEntityGetter('trajectories')
                },
                set(trajectories) {
                    this.selectedEntitySetter(trajectories, 'trajectories')
                }
            },
            selectedClients: {
                get() {
                    return this.selectedEntityGetter('clients')
                },
                set(clients) {
                    this.selectedEntitySetter(clients, 'clients')
                }
            },
        },
        methods: {
            isCoach() {
                return this.$hasType('coach');
            },
            decimalToDutchTime(decimalHours) {
                if (isNaN(decimalHours)) return '';

                const hours = Math.floor(decimalHours);
                const minutes = Math.round((decimalHours - hours) * 60);

                let parts = [];
                if (hours > 0) {
                    parts.push(`${hours} u`);
                }
                if (minutes > 0) {
                    parts.push(`${minutes} m`);
                }
                if (parts.length === 0) {
                    return '0 m';
                }
                return parts.join(' ');
            },

            handleCustomOptionProject() {
                this.filters.projects = this.availableFilterOptions.projects.map(p => p.id);
                this.$refs.multiSelectProject.deactivate();
                this.updateData();
            },
            handleCustomOptionTrajectory() {
                this.filters.trajectories = this.availableFilterOptions.trajectories.map(p => p.id);
                this.$refs.multiSelectTrajectory.deactivate();
                this.updateData();
            },
            handleCustomOptionClient() {
                this.filters.clients = this.availableFilterOptions.clients.map(p => p.id);
                this.$refs.multiSelectClient.deactivate();
                this.updateData(true);
            },
            handleCustomOptionRegion() {
                this.filters.regions = this.availableFilterOptions.regions.map(p => p.id);
                this.$refs.multiSelectRegion.deactivate();
                this.updateData(true);
            },
            handleCustomOptionTrajectoryType() {
                this.filters.trajectory_types = this.availableFilterOptions.trajectory_types.map(p => p.id);
                this.$refs.multiSelectTrajectoryType.deactivate();
                this.updateData(true);
            },
            
            sortData() {
                const getSortValue = (row, col) => {
                    switch (col) {
                        case 'enrolment_id':
                            return Number(row.enrolment_id) || 0;

                        case 'text_candidate_name': {
                            const name = [row.first_letters, row.first_name, row.prefix, row.last_name]
                                .filter(Boolean)
                                .join(' ')
                                .toLowerCase();
                            return name;
                        }

                        case 'text_trajectory_name':
                            return (row.trajectory_name || '').toLowerCase();

                        case 'text_project_name':
                            return (row.trajectory_project_name || '').toLowerCase();

                        case 'text_time_logged':
                            return Number(row.time_logged) || 0;

                        default:
                            return '';
                    }
                };

                this.enrolments = [...this.enrolments].sort((a, b) => {
                    const A = getSortValue(a, this.sortColumn);
                    const B = getSortValue(b, this.sortColumn);

                    if (A < B) return this.sortOrder === 'asc' ? -1 : 1;
                    if (A > B) return this.sortOrder === 'asc' ?  1 : -1;
                    return 0;
                });
            },
            
            getTextInPercent(number) {
                if (typeof number === "number") {
                    return number.toFixed(0) + "%";
                } else {
                    return "N/A";
                }
            },

            defaultFilters() {
                if (localStorage.getItem("filtersSavedSelectedDatePresetTimeLogs"))
                {
                    const preset = JSON.parse(localStorage.getItem("filtersSavedSelectedDatePresetTimeLogs"));
                    this.applyDatePreset(this.date_filter_presets[preset]?.months, preset);
                }
                else
                {
                    this.applyDatePreset(3, 'three')
                }
                if (localStorage.getItem("filtersSavedTimeLogs"))
                {
                    this.filters = JSON.parse(localStorage.getItem("filtersSavedTimeLogs"));
                    this.filters.date_from = null;
                    this.filters.date_to = null;

                    if(this.filters.date_from === "1970-01-01T00:00:00.000Z") {
                        this.filters.date_from = null;
                    } else {
                        this.filters.date_from = new Date(this.filters.date_from);
                    }
                    if(this.filters.date_to === "1970-01-01T00:00:00.000Z") {
                        this.filters.date_to = null;
                    } else {
                        this.filters.date_to = new Date(this.filters.date_to);
                    }
                }
                else {
                    this.filters.date_from = null;
                    this.filters.date_to = null;
                }

                console.log('Default filters applied:', this.filters);

                this.updateData(true)
            },
            limitText: (entity) => count => uLang('app.multiselect_overflow', {
                count,
                entity: lang(`app.${ entity }`).toLowerCase()
            }),
            selectedEntityGetter(entityName) {
                return this.filters[entityName].map(entityId => this.availableFilterOptions[entityName].find(entity => entity.id === entityId))
            },
            selectedEntitySetter(entity, entityName) {
                this.filters[entityName] = entity.map(entity => entity.id)
            },
            applyDatePreset(months, key) {
                this.selected_date_preset = key

                if (key === 'all') {
                    this.filters.date_from = null
                    this.filters.date_to = null
                }

                if (months === 3 || months === 6) {
                    if(months === 3 ) {
                        this.filters.date_from = moment().startOf('year').toDate();
                        this.filters.date_to = moment().endOf('year').toDate();
                    } else if(months === 6) {
                        this.filters.date_from = moment().subtract(1, 'year').startOf('year').toDate();
                        this.filters.date_to = moment().subtract(1, 'year').endOf('year').toDate();
                    }
                } else {
                    this.filters.date_from = months ? moment().subtract(months, 'months').toDate() : null;
                    this.filters.date_to = months ? moment().toDate() : null;
                }
                this.updateData()
            },
            
            filterOnlyWithTimeLogsStatus(status) {
                this.filters.only_with_time_logs = status;
                this.updateData()
            },

            clearFilter(name, value = null) {
                this.filters[name] = value
                this.updateData()
            },
            async fetchData() {
                this.loading = true;

                if(this.filters.date_from === "1970-01-01T00:00:00.000Z" || this.filters.date_from?.toString() === (new Date(0)).toString()) {
                    this.filters.date_from = null;
                }
                if(this.filters.date_to === "1970-01-01T00:00:00.000Z" || this.filters.date_to?.toString() === (new Date(0)).toString()) {
                    this.filters.date_to = null;
                }

                let filters = JSON.stringify({
                    ...this.filters,
                    date_from: this.filters.date_from ? this.filters.date_from?.toDateString() ?? null : null,
                    date_to: this.filters.date_to ? this.filters.date_to?.toDateString() ?? null : null,
                })

                console.log('Fetching data with filters:', filters);

                const { data } = await window.axios.get(route('management-info.results-enrolments-timelogs', { filters }))


                console.log('Fetched data:', data);

                this.enrolments = data;

                this.getAllStatistic(data);
                this.sortData();
                this.loading = false;
            },

            getAllStatistic(enrolments) {
                this.num_all_enrolments = enrolments.length;
                let sum_time_logged = 0;

                enrolments.forEach(enrolment => {
                    sum_time_logged += parseFloat(enrolment.time_logged) ?? 0;
                });

                this.sum_time_logged = sum_time_logged;
                this.average_time_logged = this.num_all_enrolments > 0 ? (sum_time_logged / this.num_all_enrolments).toFixed(2) : 0;
                
            },

            sortTable(column) {
                if (this.sortColumn === column) {
                    this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortColumn = column;
                    this.sortOrder = 'asc';
                }
            },

            async getTrajectoryTypes() {
                const { data } = await window.axios.get(route('settings.values.index', { setting: 12 }))

                data.push({ id: null, value: this.uLang('app.trajectories_without_type'), system_name: null })

                const trajectory_type = data.reduce((result, type) => {
                    this.availableFilterOptions.trajectory_types.push({
                        id: type.id,
                        name: type.value,
                        system_name: type.system_name
                    })

                    if (type.system_name !== 'quotation_phase') {
                        result.push(type.id);
                    }

                    return result;
                }, []);

                if (!localStorage.getItem("filtersSavedTimeLogs")) {
                    this.filters.trajectory_types = trajectory_type;
                }
            },
            updateData: debounce(function (test) {
                this.recalculateData(test)
            }, 300),

            async recalculateData() {
                this.loadingFilters = true

                if(this.filters.date_from === "1970-01-01T00:00:00.000Z" || this.filters.date_from?.toString() === (new Date(0)).toString()) {
                    this.filters.date_from = null;
                }
                if(this.filters.date_to === "1970-01-01T00:00:00.000Z" || this.filters.date_to?.toString() === (new Date(0)).toString()) {
                    this.filters.date_to = null;
                }

                // Prepare filters
                let filters = JSON.stringify({
                    ...this.filters,
                    date_from: this.filters.date_from ? this.filters.date_from?.toDateString() ?? null : null,
                    date_to: this.filters.date_to ? this.filters.date_to?.toDateString() ?? null : null,
                })

                const { data } = await window.axios.get(route('management-info.result-filters', { filters }))
                
                let { projects, trajectories, clients } = data;

                // Clear unavailable options from filters
                this.filters.projects = this.filters.projects.filter((el) => projects.map(p => p.id).includes(el));
                this.filters.trajectories = this.filters.trajectories.filter((el) => trajectories.map(p => p.id).includes(el));
                this.filters.clients = this.filters.clients.filter((el) => clients.map(p => p.id).includes(el));

                // Set available filter options
                this.availableFilterOptions.projects = projects
                this.availableFilterOptions.trajectories = trajectories
                this.availableFilterOptions.clients = clients

                this.loadingFilters = false

                localStorage.setItem("filtersSavedTimeLogs", JSON.stringify(this.filters));
                localStorage.setItem("filtersSavedSelectedDatePresetTimeLogs", JSON.stringify(this.selected_date_preset));
                await this.fetchData()
            },
        }
    }
</script>

<style lang="scss" scoped>
    .multiselect__element__idplein{
        background-color: #ffe9f6;
    }
    .multiselect__element__idplein:hover{
        color:white;
        background-color: #c1006d;
        font-weight: bold;
    }

    ::v-deep .multiselect__content-wrapper {
        width: max-content;
        min-width: 100%;
        max-width: 720px;
        overflow-x: auto;
    }

    ::v-deep .multiselect__content {
        min-width: 100%;
        width: max-content;
    }

    ::v-deep .multiselect__option {
        white-space: nowrap;
    }

    ::v-deep .multiselect__option::after {
        display: none;
        content: none;
    }

    .plain-card ::v-deep .multiselect__tags {
        height: 35px !important;
        overflow: hidden;
        line-height: 35px;
    }

    .plain-card ::v-deep .multiselect__tag {
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }

    .plain-card ::v-deep .multiselect__tag > span {
        display: inline-block;
        max-width: 132px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: middle;
    }
    .pressed {
        background-color: #c1006d;
        font-weight: bold;
        text-decoration: underline;
    }
    .datePresetButton {
        border: 1px solid white;
    }
    .fa-caret-down {
        transition: transform .3s ease;

        &.active {
            -webkit-transform: rotate(-180deg);
            -moz-transform: rotate(-180deg);
            -o-transform: rotate(-180deg);
            -ms-transform: rotate(-180deg);
            transform: rotate(-180deg);
        }
    }

    .plain-card {
        background-color: white;
        padding: 20px;
        position: relative;
        box-shadow: 3px 1px 5px #ccc;
        &:not(:first-child) {
            margin-top: 30px;
        }
        h2 {
            margin-bottom: 20px;
        }
        table {
            width: 100%;
            td, th {
                padding: 6px;
                text-align: left;
            }
            .divider {
                width: 1%;
                border: none;
            }
            .result {
                width: 6%;
            }
        }
    }

    .plain-card-2 {
        background-color: white;
        padding: 20px;
        position: relative;
        box-shadow: 3px 1px 5px #ccc;
        table {
            th.icon-sort-header {
                padding-right: 14px;
                cursor: pointer;
            }
            th:nth-child(1), th:nth-child(2) {
                text-align: left;
            }
            th:nth-child(3), th:nth-child(4), th:nth-child(5), th:nth-child(8) {
                text-align: center;
            }
            th:nth-child(6), th:nth-child(7) {
                text-align: left;
            }
            .summary-1 th:nth-child(2), 
            .summary-2 th:nth-child(2) {
                text-align: center;
            }

            tbody {
                td:nth-child(1), td:nth-child(2) {
                    text-align: left;
                }
                td:nth-child(3), td:nth-child(4), td:nth-child(5), td:nth-child(8) {
                    text-align: center;
                }
                td:nth-child(6), td:nth-child(7) {
                    text-align: left;
                }

                td:nth-child(1), td:nth-child(5) {
                    min-width: 130px;
                }

                td:nth-child(2){
                    min-width: 300px;
                }

                tr > td > i {
                    display: inline;
                    width: 0;
                    color: #c1006d;
                }
            }
        }
    }
    span.sorting-symbol-report-coach {
        color: #c1006d;
    }
    .multiselect__clear {
        position: absolute;
        right: 41px;
        height: 40px;
        width: 40px;
        display: block;
        cursor: pointer;
        z-index: 2;
    }
    .multiselect__clear::before {
        transform: rotate(45deg);
    }
    .multiselect__clear::after {
        transform: rotate(-45deg);
    }
    .multiselect__clear::after, .multiselect__clear::before {
        content: "";
        display: block;
        position: absolute;
        width: 3px;
        height: 16px;
        background: #aaa;
        top: 12px;
        right: 4px;
    }




    .table__heading {
        height: auto !important;
        z-index: 10;
        position: relative;
        background-color: white;
    }

    .icon-sort {
        position: relative;
        margin-left: 3px;
    }

    .icon-sort::before, .icon-sort::after {
    content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 0; 
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
    }

    .icon-sort::before {
        top: 20%;
        border-bottom: 5px solid #808080;
    }

    .icon-sort::after {
        top: 60%;
        border-top: 5px solid #808080;
    }

    .icon-sort.-ascending::before {
        border-bottom: 5px solid #c1006d;
    }

    .icon-sort.-descending::after {
        border-top: 5px solid #c1006d;
    }

    table.customer-following-system-table td:first-child {
        font-weight: normal;
    }

    .plain-card-2 .table {
    width: 100% !important;
    table-layout: fixed;
}

.plain-card-2 .table th,
.plain-card-2 .table td {
    width: auto; /* allow even distribution */
}
</style>
